<!-- Показывает аватар и, при необходимости, основные данные пользователя. -->
<template>
  <div
    class="user-identity-card"
    :class="[
      `user-identity-card--${size}`,
      { compact, 'avatar-only': avatarOnly },
    ]"
  >
    <div class="user-avatar" :style="{ background: avatarColor }">
      {{ avatarInitials }}
    </div>
    <div v-if="!avatarOnly" class="user-content">
      <strong class="user-name">{{ displayName }}</strong>
      <span v-if="description" class="user-description">
        {{ description }}
      </span>
    </div>
  </div>
</template>

<script>
import { useCompanyUserStore } from "@/stores/companyUserStore";
import { formatUserInitials } from "@/utils/user/identityUtils";

export default {
  name: "UserIdentityCard",

  props: {
    user: {
      type: Object,
      default: null,
    },
    name: {
      type: String,
      default: "",
    },
    userId: {
      type: [String, Number],
      default: null,
    },
    description: {
      type: String,
      default: "",
    },
    fallbackName: {
      type: String,
      default: "—",
    },
    fallbackInitials: {
      type: String,
      default: "—",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    avatarOnly: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md"].includes(value),
    },
  },

  computed: {
    displayName() {
      return this.name || this.userDisplayName || this.fallbackName;
    },

    userDisplayName() {
      if (!this.user) return "";

      return (
        this.user.initials ||
        this.user.full_name ||
        this.user.name ||
        this.nestedUserDisplayName
      );
    },

    nestedUserDisplayName() {
      if (!this.user?.user) return "";

      const user = this.user.user;
      const fullName = [
        user.last_name,
        user.first_name,
        user.middle_name,
      ]
        .filter(Boolean)
        .join(" ");

      return user.initials || fullName || user.email || user.phone || "";
    },

    resolvedUserId() {
      return (
        this.userId ||
        this.user?.id ||
        this.user?.user_id ||
        this.user?.user?.id ||
        null
      );
    },

    avatarInitials() {
      const source =
        this.user?.initials ||
        this.name ||
        this.userDisplayName ||
        this.fallbackInitials;

      return this.getInitials(source);
    },

    avatarColor() {
      return useCompanyUserStore().getUserColor(this.resolvedUserId);
    },
  },

  methods: {
    getInitials(value) {
      return formatUserInitials(value, this.fallbackInitials);
    },
  },
};
</script>

<style scoped>
.user-identity-card {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

.user-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.user-name {
  color: var(--color-dark);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-description {
  color: #999;
  font-size: 12px;
  line-height: 1.25;
}

.user-identity-card.compact {
  gap: 8px;
}

.user-identity-card.avatar-only {
  gap: 0;
}

.user-identity-card--sm .user-avatar {
  width: 18px;
  height: 18px;
  font-size: 8px;
}
</style>
